import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { Dropdown } from "primeng/dropdown";
import { environment } from "src/environments/environment";

import { NsiTnvedDto, TradeItemDto } from "@app/user/marks/marks-store/marks-types";
import { TranslationService } from "@core/translation.service";
import { OverlayService } from "@core/overlay.service";
import { DocumentType } from "@helper/abstraction/documents";

@Component({
  selector: "app-search-items-list",
  templateUrl: "./search-items-list.component.html",
  styleUrls: ["./search-items-list.component.scss"]
})
export class SearchItemsListComponent implements OnChanges {
	@Input() public data: TradeItemDto[] = [];
	@Input() public notFound = false;
	@Input() public gtinMode = false;
	@Input() public tabMode = false;
	@Input() public documentType?: DocumentType;
	@Input() public tNVEDCodes?: string[] = [];
	@Input() public tNVEDList?: NsiTnvedDto[] = [];
	@Input() public tab?: string;
	@Output() public appSelect: EventEmitter<TradeItemDto> = new EventEmitter<TradeItemDto>();
  @ViewChild("dropdown") dropdown?: Dropdown;

  public selectRow?: TradeItemDto;
  public isTnvedCodeError = false;
  public isCustomCodeError = false;
  public customCode: string | { id: number, name: string } | null = null;
  public tnvedCode: string | null = null;
  public editedTnvedCode: string | null = null;
  public env = environment;
  public newCustomCodesSets?: { id: number; name: string }[];
  public editableFields = {
		tnvedCode: false,
		customCode: false
	}

  constructor(
    private overlayService: OverlayService,
    private translationService: TranslationService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"]) {
      this.newCustomCodesSets = [];
      this.tnvedCode = null;
      this.editedTnvedCode = null;
    }
    if (!this.data?.length) {
      this.selectRow = undefined;
      this.customCode = null;
      this.editableFields.tnvedCode = false;
      this.editableFields.customCode = false;
      this.isTnvedCodeError = false;
      this.isCustomCodeError = false;
    }
  }

  public onRowSelect(event: any): void {
    if (this.selectRow?.gtin === event.data.gtin) {
      return;
    }
    if (this.selectRow?.gtin && this.selectRow?.gtin !== event.data.gtin) {
      this.customCode = null;
      this.isTnvedCodeError = false;
      this.isCustomCodeError = false;
      this.editableFields.tnvedCode = false;
      this.editableFields.customCode = false;
      this.tnvedCode = null;
      this.editedTnvedCode = null;
      this.newCustomCodesSets = [];
    }
    this.selectRow = { ...event.data, isTraceable: this.handleCodes(event.data.tnvedCode, event.data.extraCustomsCode) } as TradeItemDto;
    if (this.findCustomCodes(this.selectRow.tnvedCode).length && this.findCustomCodes(this.selectRow.tnvedCode).includes(this.selectRow.extraCustomsCode)) {
      this.customCode = this.findCustomCodes(this.selectRow.tnvedCode).length === 1
                          ? this.selectRow.extraCustomsCode
                          : this.findCustomCodes(this.selectRow.tnvedCode).map((el: string, i: number) => { return { id: i, name: el  }}).find(item => item.name === this.selectRow?.extraCustomsCode);
    }
    if (this.findCustomCodes(this.selectRow.tnvedCode).length && !this.findCustomCodes(this.selectRow.tnvedCode).includes(this.selectRow.extraCustomsCode)) {
      this.customCode = this.selectRow.extraCustomsCode;
    }
    if (!this.findCustomCodes(this.selectRow.tnvedCode).length) {
      this.customCode = this.selectRow.extraCustomsCode;
    }
    this.editableFields.customCode = !!!this.selectRow?.extraCustomsCode;
  }

  public handleCodes(tnvedCode: string, customCode?: string): boolean {
    const findTnved = this.findCustomCodes(tnvedCode);
    const cCode = this.checkCustomCode();

    if (!findTnved?.length || findTnved.length && !findTnved.includes(this.checkCustomCode())) {
      return false;
    }
    if (findTnved.length && cCode) {
      return findTnved.includes(cCode);
    }
    return false;
  }

  public unblockEditable(field: string): void {
    (this.editableFields as any)[field] = !(this.editableFields as any)[field];
  }

  public selectCurrentRow(): void {
    if (this.checkCodeLength("customCode")) {
      this.overlayService.showNotification$(this.translationService.getTranslation("customCodeSearchItemPopup"), "error", undefined, undefined, true);
      return;
    }
    const customCodesSets = this.newCustomCodesSets ?? this.selectRow?.customCodesSets;
    let mapItem = {
      ...this.selectRow,
      extraCustomsCode: this.customCode,
      isTraceable: this.handleCodes(this.editedTnvedCode ? this.editedTnvedCode : this.selectRow?.tnvedCode!, this.selectRow?.extraCustomsCode),
      isEditableValue: this.checkCustomCode() !== this.selectRow?.extraCustomsCode && customCodesSets?.some(el => el.name !== this.checkCustomCode()),
      tnvedCode: this.tnvedCode ?? this.selectRow?.tnvedCode,
    };
    if (this.documentType !== "EWAYBILL" && !mapItem.isTraceable) {
      this.isCustomCodeError = true;
      this.overlayService.showNotification$(this.translationService.getTranslation("customCodeSearchItemError"), "error", undefined, undefined, true);
      return;
    }
    this.isCustomCodeError = false;

    if (mapItem.isEditableValue) {
      mapItem.extraCustomsCode = { id: 0, name: mapItem?.extraCustomsCode as string };
    }
    mapItem.okrbDto = this.selectRow?.okrbDto || this.tNVEDList?.find(el => el.code === mapItem.tnvedCode)?.okrbDto;
		this.appSelect.emit(mapItem as TradeItemDto);
  }

  public editInputComplete(event: any, fieldName: "tnvedCode" | "customCode"): void {
    if ((fieldName === "tnvedCode" && ((!this.tnvedCode && event === this.selectRow?.tnvedCode) || this.tnvedCode === event) && event.length === 10)) {
      this[fieldName] = event;
      this.editedTnvedCode = event;
      return;
    }
    this[fieldName] = event;
    if (fieldName === "tnvedCode") {
      this.customCode = null;
      this.editedTnvedCode = event;
    }
    this.checkCodeLength(fieldName);
    if (this.tNVEDCodes?.includes(event)) {
      const customCodesSets = this.findCustomCodes(event)?.map((el: string, i: number) => { return { id: i, name: el } });
      this.newCustomCodesSets = customCodesSets;
      this.customCode = null;
      this.dropdown?.writeValue(null);
      this.cdr.detectChanges();
    }
  }

	public changeDropdownValue(event: any): void {
    this.customCode = event.value.name ?? event.value;
    this.isCustomCodeError = !!this.checkCustomCode()?.length;
    this.checkCodeLength("customCode");
	}

  public findCustomCodes(tnvedCode: string): string[] {
    const findTnved = this.tNVEDList?.find(el => el.code === tnvedCode)?.customCodeSet;

    return findTnved || [];
  }

  public vieField(): boolean {
    const tab = this.tab;

    if (['ME'].includes(this.env.project)) {
      return false;
    }
    if (['BM'].includes(this.env.project) && tab !== "0") {
      return false;
    }
    return true;
  }

  public checkTooltipTraceable(tnvedCode: string): boolean {
    if (!this.tNVEDCodes?.length || this.tNVEDCodes?.length && !this.tNVEDCodes?.includes(tnvedCode) || this.documentType !== "EWAYBILL") {
      return false;
    }
    return !this.handleCodes(tnvedCode);
  }

  public checkTooltipMsg(tnved?: string): string {
    return this.translationService.getTranslation("toolTipTNVedMsg")?.replace("TNVED", tnved!);
  }

  private checkCodeLength(fieldName: "tnvedCode" | "customCode"): boolean {
    if (!this[fieldName]) {
      return false;
    }
    const codeLength = fieldName === "customCode"
      ? this.checkCustomCode()?.length
      : (this[fieldName] as string).length;

    const codesLength = { tnvedCode: 10, customCode: 4 };
    const errorName = fieldName === "customCode" ? "isCustomCodeError" : "isTnvedCodeError";
    this[errorName] = ![0, codesLength[fieldName]].includes(codeLength);

    return this[errorName];
  }

  private checkCustomCode(): string {
    return typeof this.customCode === "string"
      ? this.customCode
      : this.customCode?.name!;
  }
}
